/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { eventFieldsDynamic } from "../../../../constants/forms/eventFields";
import {
  getMissingRequiredFields,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { v4 as uuidv4 } from "uuid";
import useNotification from "../../../../hooks/useNotification";
import SimpleBody from "../../../../components/forms/SimpleBody";
import useBaseService from "../../../../hooks/services/useBaseService";
import { useNavigate } from "react-router-dom";
import useRemoteSelectOptions from "../../../../hooks/useRemoteSelectOptions";
import useSelectOptions from "../../../../hooks/useSelectOptions";
import { entities } from "../../../../constants/entities";
import useForm from "../../../../hooks/useForm";
import { useSectionFields } from "../../../../hooks/useSectionFields";

export const useEventPageLogic = ({ contractId, isEditPage = false }) => {
  const navigate = useNavigate();

  const {
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { updateField: updateProps, removeFields } = useSectionFields();

  const { showNotification } = useNotification();

  const [
    eventSection,
    aditionRegisterSection,
    aditionTermSection,
    aditionQuantitySection,
    aditionPriceSection,
    aditionSupressionSection,
    aditionModifyItemSection,
    aditionContractAmendmentSection,
  ] = eventFieldsDynamic;

  const [aditionQuantity, setAditionQuantity] = useState([]);
  const [aditionPrice, setAditionPrice] = useState([]);
  const [aditionSupression, setAditionSupression] = useState([]);
  const [aditionModifyItem, setAditionModifyItem] = useState([]);

  const [aditionContractAmendmentItems, setAditionContractAmendmentItems] =
    useState([]);
  const [aditionContractAmendmentJobs, setAditionContractAmendmentJobs] =
    useState([]);

  const [aditionRegisterItems, setAditionRegisterItems] = useState([]);
  const [aditionRegisterJobs, setAditionRegisterJobs] = useState([]);

  const {
    formData: eventFormData,
    handleChange: handleEventChange,
    setFormData: setEventFormData,
    resetForm: resetEventForm,
  } = useForm(setDefaultFieldValues([eventSection]), [eventSection]);

  const [eventFieldsState, setEventFieldsState] = useState(eventSection.fields);

  // * Se estiver sendo usado na página de edição, devemos bloquear a opção tipo de evento
  useEffect(() => {
    let updateEventFields = [...eventSection.fields];

    if (isEditPage) {
      updateEventFields = updateProps({
        arrayDependente: updateEventFields,
        field_id: ["event.contract_event_type_id"],
        updates: {
          disabled: true,
        },
      });

      setEventFieldsState(updateEventFields);
    }

    setEventFormData(updateEventFields);
  }, [isEditPage]);

  const eventTypesSelect = useRemoteSelectOptions({
    queryKey: ["eventTypesOptions"],
    baseServiceGet,
    columnDataBase: ["name"],
    entityPath: entities.contractEventTypes.get,
    extraFilters: {
      name: {
        $ne: "CADASTRO",
      },
    },
    fieldId: "event.contract_event_type_id",
    formData: eventFormData,
    isMulti: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (eventType) => ({
      value: eventType.id,
      label: eventType.name,
    }),
  });

  const eventSelects = {
    "event.contract_event_type_id": eventTypesSelect,
  };

  const getEventOptions = useCallback(
    (fieldId) => {
      const selectConfig = eventSelects[fieldId];
      return selectConfig?.options || [];
    },
    [eventSelects],
  );

  const getEventSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = eventSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [eventSelects],
  );

  const getEventSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = eventSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [eventSelects],
  );

  const {
    formData: contractFormData,
    handleChange: handleContractChange,
    setFormData: setContractFormData,
  } = useForm(setDefaultFieldValues([aditionRegisterSection]), [
    aditionRegisterSection,
  ]);

  const contractTypesSelect = useRemoteSelectOptions({
    queryKey: ["contractTypesOptions"],
    baseServiceGet,
    columnDataBase: ["name"],
    entityPath: entities.contractTypes.get,
    fieldId: "contract.contract_type_id",
    formData: contractFormData,
    perPage: 15,
    initialPage: 1,
    immediateFetch: !!isEditPage,
    enablePagination: true,
    mapToOption: (type) => ({
      value: type.id,
      label: type.name,
    }),
  });

  const contractStatusSelect = useRemoteSelectOptions({
    queryKey: ["contractStatusOptions"],
    baseServiceGet,
    columnDataBase: ["name"],
    entityPath: entities.contractStatus.get,
    fieldId: "contract.contract_status_id",
    formData: contractFormData,
    perPage: 15,
    initialPage: 1,
    immediateFetch: !!isEditPage,
    enablePagination: true,
    mapToOption: (type) => ({
      value: type.id,
      label: type.name,
    }),
  });

  const organizationSelect = useRemoteSelectOptions({
    queryKey: ["organizationOptions"],
    baseServiceGet,
    columnDataBase: ["name"],
    entityPath: entities.organizations.get,
    fieldId: "contract.organization_id",
    formData: contractFormData,
    perPage: 15,
    initialPage: 1,
    immediateFetch: !!isEditPage,
    enablePagination: true,
    mapToOption: (type) => ({
      value: type.id,
      label: type.name,
    }),
  });

  const customerSelect = useRemoteSelectOptions({
    queryKey: ["customerOptions"],
    baseServiceGet,
    columnDataBase: ["name"],
    entityPath: entities.customers.get,
    extraFilters: {
      alias: { $nin: ["ALUCOM", "MOREIA", "IP", "AS SISTEMAS"] },
    },
    fieldId: "contract.customer_id",
    formData: contractFormData,
    perPage: 15,
    initialPage: 1,
    immediateFetch: !!isEditPage,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
  });

  const contractSelects = {
    "contract.contract_type_id": contractTypesSelect,
    "contract.contract_status_id": contractStatusSelect,
    "contract.organization_id": organizationSelect,
    "contract.customer_id": customerSelect,
  };

  const getContractOptions = useCallback(
    (fieldId) => {
      const selectConfig = contractSelects[fieldId];
      return selectConfig?.options || [];
    },
    [contractSelects],
  );

  const getContractSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = contractSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [contractSelects],
  );

  const getContractSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = contractSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [contractSelects],
  );

  const {
    formData: aditionRegisterItemsFormData,
    handleChange: handleAditionRegisterItemChange,
    setFormData: setAditionRegisterItemsFormData,
  } = useForm(setDefaultFieldValues([aditionRegisterSection.itemSection]), [
    aditionRegisterSection.itemSection,
  ]);

  const [aditionRegisterItemsFields, setAditionRegisterItemsFields] = useState(
    aditionRegisterSection.itemSection.fields,
  );

  const {
    formData: aditionRegisterJobsFormData,
    handleChange: handleAditionRegisterJobChange,
    setFormData: setAditionRegisterJobsFormData,
  } = useForm(setDefaultFieldValues([aditionRegisterSection.jobSection]), [
    aditionRegisterSection.jobSection,
  ]);

  const [aditionRegisterJobsFields, setAditionRegisterJobsFields] = useState(
    aditionRegisterSection.jobSection.fields,
  );

  // * Items - Apostilamento
  const aditionRegisterItemsSelect = useRemoteSelectOptions({
    queryKey: ["contractItemsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.items(contractId),
    mapToOption: (item) => {
      // * Fluxo de criação do evento
      // * Quando tiver na página de edição do evento, precisamos atualizar o label com o original_quantity e o original_available
      // * Para que mostre o estado anterior do item
      if (isEditPage && aditionContractAmendmentItems.length > 0) {
        const findContractItemModified = aditionContractAmendmentItems.find(
          (adition) => adition.root_item_id === item.root_item_id,
        );

        if (findContractItemModified) {
          return {
            value: item.id,
            label: `${findContractItemModified.original_alias} - ${findContractItemModified.original_description}`,
          };
        }

        return {
          value: item.id,
          label: `${item.alias} - ${item.description}`,
        };
      }

      return {
        value: item.id,
        label: `${item.alias} - ${item.description}`,
      };
    },
    enabled: !!contractId,
    immediateFetch: !!isEditPage,
    fieldId: "items.contract_item_id",
    formData: aditionRegisterItemsFormData,
    setFormData: setAditionRegisterItemsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionRegisterJobsSelect = useRemoteSelectOptions({
    queryKey: ["contractJobsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.jobs(contractId),
    mapToOption: (item) => {
      // * Fluxo de criação do evento
      // * Quando tiver na página de edição do evento, precisamos atualizar o label com o original_quantity e o original_available
      // * Para que mostre o estado anterior do item
      if (isEditPage) {
        const findJobModified = aditionContractAmendmentJobs.find(
          (adition) => adition.root_job_id === item.root_job_id,
        );

        if (findJobModified) {
          return {
            value: item.id,
            label: `${findJobModified.original_alias} - ${findJobModified.original_description}`,
          };
        }
        return {
          value: item.id,
          label: `${item.alias} - ${item.description}`,
        };
      }

      // * Fluxo de criação do evento
      return {
        value: item.id,
        label: `${item.alias} - ${item.description}`,
      };
    },
    enabled: !!contractId,
    fieldId: "jobs.contract_job_id",
    immediateFetch: !!isEditPage,
    formData: aditionRegisterJobsFormData,
    setFormData: setAditionRegisterJobsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionRegisterItemsSelects = {
    "items.contract_item_id": aditionRegisterItemsSelect,
  };

  const aditionRegisterJobsSelects = {
    "jobs.contract_job_id": aditionRegisterJobsSelect,
  };

  const aditionRegisterItemsOptions = useCallback(
    (fieldId) => {
      const selectConfig = aditionRegisterItemsSelects[fieldId];
      return selectConfig?.options || [];
    },
    [aditionRegisterItemsSelects],
  );

  const aditionRegisterItemsSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = aditionRegisterItemsSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [aditionRegisterItemsSelects],
  );

  const aditionRegisterItemsSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = aditionRegisterItemsSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [aditionRegisterItemsSelects],
  );

  const handleAditionRegisterItemsChange = useCallback(
    (newAditionRegisterItems) => {
      setAditionRegisterItems(newAditionRegisterItems);
    },
    [],
  );

  const handleAditionRegisterJobsChange = useCallback(
    (newAditionRegisterJobs) => {
      setAditionRegisterJobs(newAditionRegisterJobs);
    },
    [],
  );

  const aditionRegisterJobsOptions = useCallback(
    (fieldId) => {
      const selectConfig = aditionRegisterJobsSelects[fieldId];
      return selectConfig?.options || [];
    },
    [aditionRegisterJobsSelects],
  );

  const aditionRegisterJobsSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = aditionRegisterJobsSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [aditionRegisterJobsSelects],
  );

  const aditionRegisterJobsSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = aditionRegisterJobsSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [aditionRegisterJobsSelects],
  );

  // * Atualizar o valor total do contrato, baseado nos items e serviços
  useEffect(() => {
    let totalValue = null;
    aditionRegisterItems.forEach((item) => {
      totalValue += Number(item.original_price);
    });
    aditionRegisterJobs.forEach((job) => {
      totalValue += Number(job.original_price);
    });

    if (totalValue) {
      const duration = Number(contractFormData?.info?.duration) || 1;
      const totalAmount = Math.round(totalValue * duration * 100) / 100;

      setContractFormData({
        ...contractFormData,
        info: {
          ...contractFormData.info,
          total_amount: totalAmount,
        },
      });
    }
  }, [aditionRegisterItems, aditionRegisterJobs]);

  // * Setar as informações do item e jobs a ser modificado pelo aditivo acima
  useEffect(() => {
    const itemSelected =
      aditionRegisterItemsFormData?.items?.contract_item_id?.value;

    const jobSelected =
      aditionRegisterJobsFormData?.jobs?.contract_job_id?.value;

    if (itemSelected) {
      const mathItem = aditionRegisterItemsSelect.rawData?.find(
        (item) => item.id === itemSelected,
      );

      if (mathItem) {
        setAditionRegisterItemsFormData({
          items: {
            ...aditionRegisterItemsFormData?.items,
            alias: mathItem.alias,
            description: mathItem.description,
          },
        });
      }
    }

    if (jobSelected) {
      const mathJob = aditionRegisterJobsSelect.rawData?.find(
        (job) => job.id === jobSelected,
      );
      if (mathJob) {
        setAditionRegisterJobsFormData({
          jobs: {
            ...aditionRegisterJobsFormData?.jobs,
            alias: mathJob.alias,
            description: mathJob.description,
          },
        });
      }
    }
  }, [
    aditionRegisterItemsFormData?.items?.contract_item_id?.value,
    aditionRegisterJobsFormData?.jobs?.contract_job_id?.value,
  ]);

  const handleSaveAditionRegisterItems = useCallback(
    (itemToEdit) => {
      let updatedAditionRegisterItems;
      const itemData = aditionRegisterItemsFormData?.items || {};

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: aditionRegisterSection.itemSection?.fields,
        prefix: "items",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para cadastro de itens",
        );
        return false;
      }

      if (itemToEdit) {
        updatedAditionRegisterItems = aditionRegisterItems.map((adition) =>
          adition.id === itemToEdit.id ? { ...adition, ...itemData } : adition,
        );
      } else {
        updatedAditionRegisterItems = [
          ...aditionRegisterItems,
          { id: uuidv4().slice(0, 8), ...itemData },
        ];
      }

      setAditionRegisterItems(updatedAditionRegisterItems);
      handleAditionRegisterItemsChange(updatedAditionRegisterItems);
      return true;
    },
    [
      aditionRegisterItems,
      aditionRegisterItemsFormData?.items,
      aditionRegisterSection.itemSection?.fields,
      handleAditionRegisterItemsChange,
      showNotification,
    ],
  );

  const initializeAditionRegisterItemsForm = useCallback(
    (itemData) => {
      setAditionRegisterItemsFormData({
        items: itemData || {},
      });
    },
    [setAditionRegisterItemsFormData],
  );

  const handleSaveAditionRegisterJobs = useCallback(
    (itemToEdit) => {
      let updatedAditionRegisterJobs;
      const itemData = aditionRegisterJobsFormData?.jobs || {};

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: aditionRegisterSection.jobSection?.fields,
        prefix: "jobs",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para cadastro de serviços",
        );
        return false;
      }

      if (itemToEdit) {
        updatedAditionRegisterJobs = aditionRegisterJobs.map((adition) =>
          adition.id === itemToEdit.id ? { ...adition, ...itemData } : adition,
        );
      } else {
        updatedAditionRegisterJobs = [
          ...aditionRegisterJobs,
          { id: uuidv4().slice(0, 8), ...itemData },
        ];
      }

      setAditionRegisterJobs(updatedAditionRegisterJobs);
      handleAditionRegisterJobsChange(updatedAditionRegisterJobs);
      return true;
    },
    [
      aditionRegisterJobs,
      aditionRegisterJobsFormData?.jobs,
      aditionRegisterSection.jobSection?.fields,
      handleAditionRegisterJobsChange,
      showNotification,
    ],
  );

  const initializeAditionRegisterJobsForm = useCallback(
    (itemData) => {
      setAditionRegisterJobsFormData({
        jobs: itemData || {},
      });
    },
    [setAditionRegisterJobsFormData],
  );

  const {
    formData: aditionTermFormData,
    setFormData: setAditionTermFormData,
    handleChange: handleAditionTermChange,
  } = useForm(setDefaultFieldValues([aditionTermSection]), [
    aditionTermSection,
  ]);

  const {
    formData: aditionQuantityFormData,
    handleChange: handleAditionQuantityChange,
    setFormData: setAditionQuantityFormData,
  } = useForm(setDefaultFieldValues([aditionQuantitySection]), [
    aditionQuantitySection,
  ]);

  const [aditionQuantityFieldsState, setAditionQuantityFieldsState] = useState(
    aditionQuantitySection.fields,
  );

  const aditionQuantityNewItemSelect = useSelectOptions({
    fieldId: "aditionQuantity.new",
    formData: aditionQuantityFormData,
    rawOptions: [
      { id: true, name: "Sim" },
      { id: false, name: "Não" },
    ],
    isMulti: false,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  });

  const aditionQuantityContractItemsSelect = useRemoteSelectOptions({
    queryKey: ["contractItemsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.items(contractId),
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.alias} - ${item.description} - Qtd. Atual: ${item.quantity}`,
    }),
    enabled:
      !!contractId &&
      aditionQuantityFormData?.aditionQuantity?.new?.value === false,
    fieldId: "aditionQuantity.contract_item_id",
    formData: aditionQuantityFormData,
    setFormData: setAditionQuantityFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionQuantitySelects = {
    "aditionQuantity.new": aditionQuantityNewItemSelect,
    "aditionQuantity.contract_item_id": aditionQuantityContractItemsSelect,
  };

  const getAditionQuantityOptions = useCallback(
    (fieldId) => {
      const selectConfig = aditionQuantitySelects[fieldId];
      return selectConfig?.options || [];
    },
    [aditionQuantitySelects],
  );

  const getAditionQuantitySelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = aditionQuantitySelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [aditionQuantitySelects],
  );

  const getAditionQuantitySelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = aditionQuantitySelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [aditionQuantitySelects],
  );

  const handleAditionsQuantityChange = useCallback((newAditionQuantity) => {
    setAditionQuantity(newAditionQuantity);
  }, []);

  const handleSaveAditionQuantity = useCallback(
    (itemToEdit) => {
      let updatedAditionQuantity;
      const itemData = aditionQuantityFormData?.aditionQuantity || {};

      const isNewItem = Boolean(
        aditionQuantityFormData?.aditionQuantity?.new?.value === true,
      );

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: aditionQuantitySection.fields,
        prefix: "aditionQuantity",
        ignore: isNewItem
          ? ["aditionQuantity.contract_item_id"]
          : [
              "aditionQuantity.price",
              "aditionQuantity.description",
              "aditionQuantity.alias",
            ],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para aditivo de quantidade",
        );
        return false;
      }

      if (itemToEdit) {
        updatedAditionQuantity = aditionQuantity.map((adition) =>
          adition.id === itemToEdit.id ? { ...adition, ...itemData } : adition,
        );
      } else {
        updatedAditionQuantity = [
          ...aditionQuantity,
          { id: uuidv4().slice(0, 8), ...itemData },
        ];
      }

      setAditionQuantity(updatedAditionQuantity);
      handleAditionsQuantityChange(updatedAditionQuantity);
      return true;
    },
    [
      aditionQuantity,
      aditionQuantityFormData?.aditionQuantity,
      aditionQuantitySection.fields,
      handleAditionsQuantityChange,
      showNotification,
    ],
  );

  const initializeAditionQuantityForm = useCallback(
    (itemData) => {
      setAditionQuantityFormData({
        aditionQuantity:
          {
            ...itemData,
            new: { value: false, label: "Não" },
          } || {},
      });
    },
    [setAditionQuantityFormData],
  );

  // Se for aditivo de quantidade e for um novo item, habilita os campos de preço e descrição
  useEffect(() => {
    const isNewItem =
      aditionQuantityFormData?.aditionQuantity?.new?.value === true;
    let updatedFields = [...aditionQuantitySection.fields];

    const isJob =
      aditionQuantityFormData?.aditionQuantity?.item_or_job?.value === false;

    if (isJob) {
      // * Remover campos de contract_item_id
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: [
          "aditionQuantity.new",
          "aditionQuantity.contract_item_id",
          "aditionQuantity.price",
          "aditionQuantity.description",
          "aditionQuantity.alias",
        ],
      });

      setAditionQuantityFieldsState(updatedFields);
    } else {
      if (isNewItem) {
        updatedFields = updateProps({
          arrayDependente: aditionQuantitySection?.fields,
          field_id: ["aditionQuantity.price", "aditionQuantity.description"],
          updates: {
            disabled: false,
          },
        });

        //  * Remove o campo de contract_item_id, e adiciona o alias
        updatedFields = removeFields({
          arrayDependente: updatedFields,
          field_id: [
            "aditionQuantity.contract_item_id",
            "aditionQuantity.contract_job_id",
          ],
        });

        setAditionQuantityFieldsState(updatedFields);
      } else {
        updatedFields = removeFields({
          arrayDependente: updatedFields,
          field_id: [
            "aditionQuantity.price",
            "aditionQuantity.description",
            "aditionQuantity.alias",
            "aditionQuantity.contract_job_id",
          ],
        });

        updatedFields = updateProps({
          arrayDependente: updatedFields,
          field_id: ["aditionQuantity.quantity"],
          updates: {
            fullWidth: true,
          },
        });
        setAditionQuantityFieldsState(updatedFields);
      }
    }
  }, [
    aditionQuantityFormData?.aditionQuantity.new?.value,
    aditionQuantityFormData?.aditionQuantity?.item_or_job?.value,
  ]);

  const {
    formData: aditionPriceFormData,
    handleChange: handleAditionPriceChange,
    setFormData: setAditionPriceFormData,
  } = useForm(setDefaultFieldValues([aditionPriceSection]), [
    aditionPriceSection,
  ]);

  const [aditionPriceFieldsState, setAditionPriceFieldsState] = useState(
    aditionPriceSection.fields,
  );

  const handleAditionsPriceChange = useCallback((newAditionPrice) => {
    setAditionPrice(newAditionPrice);
  }, []);

  const aditionPriceItemOrJobSelect = useSelectOptions({
    fieldId: "aditionPrice.item_or_job",
    formData: aditionPriceFormData,
    rawOptions: [
      { value: true, label: "Item do Contrato" },
      { value: false, label: "Serviço" },
    ],
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  });

  const aditionPriceContractItemsSelect = useRemoteSelectOptions({
    queryKey: ["contractItemsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.items(contractId),
    mapToOption: (item) => {
      // * Fluxo de criação do evento
      // * Quando tiver na página de edição do evento, precisamos atualizar o label com o original_quantity e o original_available
      // * Para que mostre o estado anterior do item
      if (isEditPage) {
        const findContractItemModified = aditionPrice.find(
          (adition) => adition.root_item_id === item.root_item_id,
        );

        if (findContractItemModified) {
          return {
            value: item.id,
            label: `${item.alias} - ${item.description} - Preço Atual: R$${Number(findContractItemModified.original_price).toLocaleString("pt-BR", { minimumFractionDigits: 4 })}`,
          };
        }

        return {
          value: item.id,
          label: `${item.alias} - ${item.description} - Preço Atual: R$${Number(item.price).toLocaleString("pt-BR", { minimumFractionDigits: 4 })}`,
        };
      }

      // * Fluxo de criação do evento
      return {
        value: item.id,
        label: `${item.alias} - ${item.description} - Preço Atual: R$${Number(item.price).toLocaleString("pt-BR", { minimumFractionDigits: 4 })}`,
      };
    },
    enabled: !!contractId,
    fieldId: "aditionPrice.contract_item_id",
    formData: aditionPriceFormData,
    setFormData: setAditionPriceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionPriceJobsSelect = useRemoteSelectOptions({
    queryKey: ["contractJobsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.jobs(contractId),
    mapToOption: (item) => {
      // * Fluxo de criação do evento
      // * Quando tiver na página de edição do evento, precisamos atualizar o label com o original_quantity e o original_available
      // * Para que mostre o estado anterior do item
      if (isEditPage) {
        const findJobModified = aditionPrice.find(
          (adition) => adition.root_job_id === item.root_job_id,
        );

        if (findJobModified) {
          return {
            value: item.id,
            label: `${item.alias} - ${item.description} - Preço Atual: R$${Number(findJobModified.original_price).toLocaleString("pt-BR", { minimumFractionDigits: 4 })}`,
          };
        }

        return {
          value: item.id,
          label: `${item.alias} - ${item.description} - Preço Atual: R$${Number(item.price).toLocaleString("pt-BR", { minimumFractionDigits: 4 })}`,
        };
      }

      // * Fluxo de criação do evento
      return {
        value: item.id,
        label: `${item.alias} - ${item.description} - Preço Atual: R$${Number(item.price).toLocaleString("pt-BR", { minimumFractionDigits: 4 })}`,
      };
    },
    enabled: !!contractId,
    fieldId: "aditionPrice.contract_job_id",
    formData: aditionPriceFormData,
    setFormData: setAditionPriceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionPriceSelects = {
    "aditionPrice.item_or_job": aditionPriceItemOrJobSelect,
    "aditionPrice.contract_item_id": aditionPriceContractItemsSelect,
    "aditionPrice.contract_job_id": aditionPriceJobsSelect,
  };

  const getAditionPriceOptions = useCallback(
    (fieldId) => {
      const selectConfig = aditionPriceSelects[fieldId];
      return selectConfig?.options || [];
    },
    [aditionPriceSelects],
  );

  const getAditionPriceSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = aditionPriceSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [aditionPriceSelects],
  );

  const getAditionPriceSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = aditionPriceSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [aditionPriceSelects],
  );

  useEffect(() => {
    let updatedFields = [...aditionPriceSection.fields];

    const isJob =
      aditionPriceFormData?.aditionPrice?.item_or_job?.value === false;

    if (isJob) {
      // * Remover campos de contract_item_id
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: ["aditionPrice.contract_item_id"],
      });
    } else {
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: ["aditionPrice.contract_job_id"],
      });
    }
    setAditionPriceFieldsState(updatedFields);
  }, [aditionPriceFormData?.aditionPrice?.item_or_job?.value]);

  const handleSaveAditionPrice = useCallback(
    (itemToEdit) => {
      let updatedAditionPrice;
      const itemData = aditionPriceFormData?.aditionPrice || {};

      const isItem = Boolean(
        aditionPriceFormData?.aditionPrice?.item_or_job?.value,
      );

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: aditionPriceSection.fields,
        prefix: "aditionPrice",
        ignore: isItem
          ? ["aditionPrice.contract_job_id"]
          : ["aditionPrice.contract_item_id"],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para aditivo de preço",
        );
        return false;
      }

      if (itemToEdit) {
        updatedAditionPrice = aditionPrice.map((adition) =>
          adition.id === itemToEdit.id ? { ...adition, ...itemData } : adition,
        );
      } else {
        updatedAditionPrice = [
          ...aditionPrice,
          { id: uuidv4().slice(0, 8), ...itemData },
        ];
      }

      setAditionPrice(updatedAditionPrice);
      handleAditionsPriceChange(updatedAditionPrice);
      return true;
    },
    [
      aditionPrice,
      aditionPriceFormData?.aditionPrice,
      aditionPriceSection.fields,
      handleAditionsPriceChange,
      showNotification,
    ],
  );

  const initializeAditionPriceForm = useCallback(
    (itemData) => {
      setAditionPriceFormData({
        aditionPrice:
          {
            ...itemData,
            item_or_job: { value: true, label: "Item do Contrato" },
          } || {},
      });
    },
    [setAditionPriceFormData],
  );

  const {
    formData: aditionSupressionFormData,
    handleChange: handleAditionSupressionChange,
    setFormData: setAditionSupressionFormData,
  } = useForm(setDefaultFieldValues([aditionSupressionSection]), [
    aditionSupressionSection,
  ]);

  const [aditionSupressionFieldsState, setAditionSupressionFieldsState] =
    useState(aditionSupressionSection.fields);

  const handleAditionsSupressionChange = useCallback((newAditionSupression) => {
    setAditionSupression(newAditionSupression);
  }, []);

  const aditionSupressionContractItemsSelect = useRemoteSelectOptions({
    queryKey: ["contractItemsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.items(contractId),
    mapToOption: (item) => {
      // * Fluxo de criação do evento
      // * Quando tiver na página de edição do evento, precisamos atualizar o label com o original_quantity e o original_available
      // * Para que mostre o estado anterior do item
      if (isEditPage) {
        const findContractItemModified = aditionSupression.find(
          (adition) => adition.root_item_id === item.root_item_id,
        );
        if (findContractItemModified) {
          return {
            value: item.id,
            label: `${item.alias} - ${item.description} - Qtd. Original: ${findContractItemModified.original_quantity} - Disponível para Supressão: ${findContractItemModified.original_available}`,
          };
        }
        return {
          value: item.id,
          label: `${item.alias} - ${item.description} - Qtd. Atual: ${item.quantity} - Disponível para Supressão: ${item.available_quantity}`,
        };
      }

      // * Fluxo de criação do evento
      return {
        value: item.id,
        label: `${item.alias} - ${item.description} - Qtd. Atual: ${item.quantity} - Disponível para Supressão: ${item.available_quantity}`,
      };
    },
    enabled: !!contractId,
    fieldId: "aditionSupression.contract_item_id",
    formData: aditionSupressionFormData,
    setFormData: setAditionSupressionFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionSupressionSelects = {
    "aditionSupression.contract_item_id": aditionSupressionContractItemsSelect,
  };

  const getAditionSupressionOptions = useCallback(
    (fieldId) => {
      const selectConfig = aditionSupressionSelects[fieldId];
      return selectConfig?.options || [];
    },
    [aditionSupressionSelects],
  );

  const getAditionSupressionSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = aditionSupressionSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [aditionSupressionSelects],
  );

  const getAditionSupressionSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = aditionSupressionSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [aditionSupressionSelects],
  );

  const handleSaveAditionSupression = useCallback(
    (itemToEdit) => {
      let updatedAditionSupression;
      const itemData = aditionSupressionFormData?.aditionSupression || {};

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: aditionSupressionSection.fields,
        prefix: "aditionSupression",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para aditivo de supressão",
        );
        return false;
      }

      if (itemToEdit) {
        updatedAditionSupression = aditionSupression.map((adition) =>
          adition.id === itemToEdit.id ? { ...adition, ...itemData } : adition,
        );
      } else {
        updatedAditionSupression = [
          ...aditionSupression,
          { id: uuidv4().slice(0, 8), ...itemData },
        ];
      }

      setAditionSupression(updatedAditionSupression);
      handleAditionsSupressionChange(updatedAditionSupression);
      return true;
    },
    [
      aditionSupression,
      aditionSupressionFormData?.aditionSupression,
      aditionSupressionSection.fields,
      handleAditionsSupressionChange,
      showNotification,
    ],
  );

  const initializeAditionSupressionForm = useCallback(
    (itemData) => {
      setAditionSupressionFormData({ aditionSupression: itemData || {} });
    },
    [setAditionSupressionFormData],
  );

  const {
    formData: aditionModifyItemFormData,
    handleChange: handleAditionModifyItemChange,
    setFormData: setAditionModifyItemFormData,
  } = useForm(setDefaultFieldValues([aditionModifyItemSection]), [
    aditionModifyItemSection,
  ]);

  const aditionModifyItemContractItemsSelect = useRemoteSelectOptions({
    queryKey: ["contractItemsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.items(contractId),
    extraFilters: {
      has_movement: { $eq: false }, // * Não retornar items de contratos que estejam em alguma requisição, somente items novos
    },
    mapToOption: (item) => {
      // * Fluxo de criação do evento
      // * Quando tiver na página de edição do evento, precisamos atualizar o label com o original_quantity e o original_available
      // * Para que mostre o estado anterior do item
      if (isEditPage) {
        const findContractItemModified = aditionModifyItem.find(
          (adition) => adition.root_item_id === item.root_item_id,
        );

        if (findContractItemModified) {
          return {
            value: item.id,
            label: `${findContractItemModified.original_alias} - ${findContractItemModified.original_description} - Qtd. Atual: ${findContractItemModified.original_quantity}`,
          };
        }
        return {
          value: item.id,
          label: `${item.alias} - ${item.description} - Qtd. Atual: ${item.quantity}`,
        };
      }

      // * Fluxo de criação do evento
      return {
        value: item.id,
        label: `${item.alias} - ${item.description} - Qtd. Atual: ${item.quantity}`,
      };
    },
    enabled: !!contractId,
    immediateFetch: !!isEditPage,
    fieldId: "aditionModifyItem.contract_item_id",
    formData: aditionModifyItemFormData,
    setFormData: setAditionModifyItemFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionModifyItemSelects = {
    "aditionModifyItem.contract_item_id": aditionModifyItemContractItemsSelect,
  };

  const getAditionModifyItemOptions = useCallback(
    (fieldId) => {
      const selectConfig = aditionModifyItemSelects[fieldId];

      return selectConfig?.options || [];
    },
    [aditionModifyItemSelects],
  );

  const getAditionModifyItemSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = aditionModifyItemSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [aditionModifyItemSelects],
  );

  const getAditionModifyItemSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = aditionModifyItemSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [aditionModifyItemSelects],
  );

  const handleAditionsModifyItemChange = useCallback((newAditionModifyItem) => {
    setAditionModifyItem(newAditionModifyItem);
  }, []);

  const handleSaveAditionModifyItem = useCallback(
    (itemToEdit) => {
      let updatedAditionModifyItem;
      const itemData = aditionModifyItemFormData?.aditionModifyItem || {};

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: aditionModifyItemSection.fields,
        prefix: "aditionModifyItem",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para aditivo de modificação de item",
        );
        return false;
      }

      if (itemToEdit) {
        updatedAditionModifyItem = aditionModifyItem.map((adition) =>
          adition.id === itemToEdit.id ? { ...adition, ...itemData } : adition,
        );
      } else {
        updatedAditionModifyItem = [
          ...aditionModifyItem,
          { id: uuidv4().slice(0, 8), ...itemData },
        ];
      }

      setAditionModifyItem(updatedAditionModifyItem);
      handleAditionsModifyItemChange(updatedAditionModifyItem);
      return true;
    },
    [
      aditionModifyItem,
      aditionModifyItemFormData?.aditionModifyItem,
      aditionModifyItemSection.fields,
      handleAditionsModifyItemChange,
      showNotification,
    ],
  );

  const initializeAditionModifyItemForm = useCallback(
    (itemData) => {
      setAditionModifyItemFormData({
        aditionModifyItem: itemData || {},
      });
    },
    [setAditionModifyItemFormData],
  );

  // * Setar as informações do item a ser modificado pelo aditivo acima
  useEffect(() => {
    const itemSelected =
      aditionModifyItemFormData?.aditionModifyItem?.contract_item_id?.value;
    if (itemSelected) {
      const mathItem = aditionModifyItemContractItemsSelect.rawData?.find(
        (item) => item.id === itemSelected,
      );
      if (mathItem) {
        setAditionModifyItemFormData({
          aditionModifyItem: {
            ...aditionModifyItemFormData?.aditionModifyItem,
            alias: mathItem.alias,
            description: mathItem.description,
            quantity: mathItem.quantity,
            price: mathItem.price,
          },
        });
      }
    }
  }, [aditionModifyItemFormData?.aditionModifyItem?.contract_item_id?.value]);

  const {
    formData: aditionContractAmendmentFormData,
    handleChange: handleAditionContractAmendmentChange,
    setFormData: setAditionContractAmendmentFormData,
  } = useForm(setDefaultFieldValues([aditionContractAmendmentSection]), [
    aditionContractAmendmentSection,
  ]);

  const {
    formData: aditionContractAmendmentItemsFormData,
    handleChange: handleAditionContractAmendmentItemChange,
    setFormData: setAditionContractAmendmentItemsFormData,
  } = useForm(
    setDefaultFieldValues([aditionContractAmendmentSection.itemSection]),
    [aditionContractAmendmentSection.itemSection],
  );

  const {
    formData: aditionContractAmendmentJobsFormData,
    handleChange: handleAditionContractAmendmentJobChange,
    setFormData: setAditionContractAmendmentJobsFormData,
  } = useForm(
    setDefaultFieldValues([aditionContractAmendmentSection.jobSection]),
    [aditionContractAmendmentSection.jobSection],
  );

  // * Items - Apostilamento
  const aditionContractAmendmentItemsSelect = useRemoteSelectOptions({
    queryKey: ["contractItemsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.items(contractId),
    mapToOption: (item) => {
      // * Fluxo de criação do evento
      // * Quando tiver na página de edição do evento, precisamos atualizar o label com o original_quantity e o original_available
      // * Para que mostre o estado anterior do item
      if (isEditPage && aditionContractAmendmentItems.length > 0) {
        const findContractItemModified = aditionContractAmendmentItems.find(
          (adition) => adition.root_item_id === item.root_item_id,
        );

        if (findContractItemModified) {
          return {
            value: item.id,
            label: `${findContractItemModified.original_alias} - ${findContractItemModified.original_description}`,
          };
        }

        return {
          value: item.id,
          label: `${item.alias} - ${item.description}`,
        };
      }

      return {
        value: item.id,
        label: `${item.alias} - ${item.description}`,
      };
    },
    enabled: !!contractId,
    immediateFetch: !!isEditPage,
    fieldId: "items.contract_item_id",
    formData: aditionContractAmendmentItemsFormData,
    setFormData: setAditionContractAmendmentItemsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionContractAmendmentJobsSelect = useRemoteSelectOptions({
    queryKey: ["contractJobsOptions", contractId],
    baseServiceGet,
    entityPath: entities.contracts.jobs(contractId),
    mapToOption: (item) => {
      // * Fluxo de criação do evento
      // * Quando tiver na página de edição do evento, precisamos atualizar o label com o original_quantity e o original_available
      // * Para que mostre o estado anterior do item
      if (isEditPage) {
        const findJobModified = aditionContractAmendmentJobs.find(
          (adition) => adition.root_job_id === item.root_job_id,
        );

        if (findJobModified) {
          return {
            value: item.id,
            label: `${findJobModified.original_alias} - ${findJobModified.original_description}`,
          };
        }
        return {
          value: item.id,
          label: `${item.alias} - ${item.description}`,
        };
      }

      // * Fluxo de criação do evento
      return {
        value: item.id,
        label: `${item.alias} - ${item.description}`,
      };
    },
    enabled: !!contractId,
    fieldId: "jobs.contract_job_id",
    immediateFetch: !!isEditPage,
    formData: aditionContractAmendmentJobsFormData,
    setFormData: setAditionContractAmendmentJobsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const aditionContractAmendmentItemsSelects = {
    "items.contract_item_id": aditionContractAmendmentItemsSelect,
  };

  const aditionContractAmendmentJobsSelects = {
    "jobs.contract_job_id": aditionContractAmendmentJobsSelect,
  };

  const aditionContractAmendmentItemsOptions = useCallback(
    (fieldId) => {
      const selectConfig = aditionContractAmendmentItemsSelects[fieldId];
      return selectConfig?.options || [];
    },
    [aditionContractAmendmentItemsSelects],
  );

  const aditionContractAmendmentItemsSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = aditionContractAmendmentItemsSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [aditionContractAmendmentItemsSelects],
  );

  const aditionContractAmendmentItemsSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = aditionContractAmendmentItemsSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [aditionContractAmendmentItemsSelects],
  );

  const handleAditionContractAmendmentItemsChange = useCallback(
    (newAditionContractAmendmentItems) => {
      setAditionContractAmendmentItems(newAditionContractAmendmentItems);
    },
    [],
  );

  const handleAditionContractAmendmentJobsChange = useCallback(
    (newAditionContractAmendmentJobs) => {
      setAditionContractAmendmentJobs(newAditionContractAmendmentJobs);
    },
    [],
  );

  const aditionContractAmendmentJobsOptions = useCallback(
    (fieldId) => {
      const selectConfig = aditionContractAmendmentJobsSelects[fieldId];
      return selectConfig?.options || [];
    },
    [aditionContractAmendmentJobsSelects],
  );

  const aditionContractAmendmentJobsSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = aditionContractAmendmentJobsSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [aditionContractAmendmentJobsSelects],
  );

  const aditionContractAmendmentJobsSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = aditionContractAmendmentJobsSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [aditionContractAmendmentJobsSelects],
  );

  const handleSaveAditionContractAmendmentItems = useCallback(
    (itemToEdit) => {
      let updatedAditionContractAmendmentItems;
      const itemData = aditionContractAmendmentItemsFormData?.items || {};

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: aditionContractAmendmentSection.itemSection?.fields,
        prefix: "items",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para apostilamento de itens",
        );
        return false;
      }

      if (itemToEdit) {
        updatedAditionContractAmendmentItems =
          aditionContractAmendmentItems.map((adition) =>
            adition.id === itemToEdit.id
              ? { ...adition, ...itemData }
              : adition,
          );
      } else {
        updatedAditionContractAmendmentItems = [
          ...aditionContractAmendmentItems,
          { id: uuidv4().slice(0, 8), ...itemData },
        ];
      }

      setAditionContractAmendmentItems(updatedAditionContractAmendmentItems);
      handleAditionContractAmendmentItemsChange(
        updatedAditionContractAmendmentItems,
      );
      return true;
    },
    [
      aditionContractAmendmentItems,
      aditionContractAmendmentItemsFormData?.items,
      aditionContractAmendmentSection.itemSection?.fields,
      handleAditionContractAmendmentItemsChange,
      showNotification,
    ],
  );

  const initializeAditionContractAmendmentItemsForm = useCallback(
    (itemData) => {
      setAditionContractAmendmentItemsFormData({
        items: itemData || {},
      });
    },
    [setAditionContractAmendmentItemsFormData],
  );

  const handleSaveAditionContractAmendmentJobs = useCallback(
    (itemToEdit) => {
      let updatedAditionContractAmendmentJobs;
      const itemData = aditionContractAmendmentJobsFormData?.jobs || {};

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: aditionContractAmendmentSection.jobSection?.fields,
        prefix: "jobs",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para apostilamento de serviços",
        );
        return false;
      }

      if (itemToEdit) {
        updatedAditionContractAmendmentJobs = aditionContractAmendmentJobs.map(
          (adition) =>
            adition.id === itemToEdit.id
              ? { ...adition, ...itemData }
              : adition,
        );
      } else {
        updatedAditionContractAmendmentJobs = [
          ...aditionContractAmendmentJobs,
          { id: uuidv4().slice(0, 8), ...itemData },
        ];
      }

      setAditionContractAmendmentJobs(updatedAditionContractAmendmentJobs);
      handleAditionContractAmendmentJobsChange(
        updatedAditionContractAmendmentJobs,
      );
      return true;
    },
    [
      aditionContractAmendmentJobs,
      aditionContractAmendmentJobsFormData?.jobs,
      aditionContractAmendmentSection.jobSection?.fields,
      handleAditionContractAmendmentJobsChange,
      showNotification,
    ],
  );

  const initializeAditionContractAmendmentJobsForm = useCallback(
    (itemData) => {
      setAditionContractAmendmentJobsFormData({
        jobs: itemData || {},
      });
    },
    [setAditionContractAmendmentJobsFormData],
  );

  // * Setar as informações do item e jobs a ser modificado pelo aditivo acima
  useEffect(() => {
    const itemSelected =
      aditionContractAmendmentItemsFormData?.items?.contract_item_id?.value;

    const jobSelected =
      aditionContractAmendmentJobsFormData?.jobs?.contract_job_id?.value;

    if (itemSelected) {
      const mathItem = aditionContractAmendmentItemsSelect.rawData?.find(
        (item) => item.id === itemSelected,
      );
      if (mathItem) {
        setAditionContractAmendmentItemsFormData({
          items: {
            ...aditionContractAmendmentItemsFormData?.items,
            alias: mathItem.alias,
            description: mathItem.description,
          },
        });
      }
    }

    if (jobSelected) {
      const mathJob = aditionContractAmendmentJobsSelect.rawData?.find(
        (job) => job.id === jobSelected,
      );
      if (mathJob) {
        setAditionContractAmendmentJobsFormData({
          jobs: {
            ...aditionContractAmendmentJobsFormData?.jobs,
            alias: mathJob.alias,
            description: mathJob.description,
          },
        });
      }
    }
  }, [
    aditionContractAmendmentItemsFormData?.items?.contract_item_id?.value,
    aditionContractAmendmentJobsFormData?.jobs?.contract_job_id?.value,
  ]);

  // * Renderização dos form dinâmicos de aditivo de quantidade, preço e supressão é controlada por meio do estado local (aditionQuantity, aditionPrice, aditionSupression) e dos formulários específicos para cada tipo de aditivo. As funções handleAditionsQuantityChange, handleAditionsPriceChange e handleAditionsSupressionChange são responsáveis por atualizar tanto o estado local quanto o formulário principal (eventFormData) sempre que uma alteração é feita em um item de aditivo. As funções handleSaveAditionQuantity, handleSaveAditionPrice e handleSaveAditionSupression validam os campos obrigatórios, atualizam o estado local e o formulário principal com os novos dados, e retornam um booleano indicando se a operação foi bem-sucedida ou não. As funções initializeAditionQuantityForm, initializeAditionPriceForm e initializeAditionSupressionForm são usadas para preencher os formulários de aditivo com os dados de um item existente quando o usuário deseja editar um aditivo já cadastrado.
  const renderAditionTermFormBody = useCallback(
    ({
      fields,
      formData,
      handleFieldChange,
      formErrors,
      getOptions,
      getSelectedValue,
      getSelectMeta,
    }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getOptions}
          getSelectedValue={getSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      );
    },
    [],
  );

  const renderAditionQuantityFormBody = useCallback(
    ({
      fields,
      formData,
      handleFieldChange,
      formErrors,
      getOptions,
      getSelectedValue,
      getSelectMeta,
    }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getOptions}
          getSelectedValue={getSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      );
    },
    [],
  );

  const renderAditionPriceFormBody = renderAditionTermFormBody;
  const renderAditionSupressionFormBody = renderAditionTermFormBody;
  const renderAditionModifyItemFormBody = renderAditionTermFormBody;
  const renderAditionContractAmendmentItemsFormBody = renderAditionTermFormBody;
  const renderAditionContractAmendmentJobsFormBody = renderAditionTermFormBody;
  const renderAditionRegisterItemsFormBody = renderAditionTermFormBody;
  const renderAditionRegisterJobsFormBody = renderAditionTermFormBody;

  const getProductDisplayValue = useCallback((item, fieldId) => {
    const fieldName = fieldId.split(".").pop();
    const value = item[fieldName];

    if (value && typeof value === "object" && "label" in value) {
      return value.label;
    }

    return value || "";
  }, []);

  const getAditionQuantityDisplayValue = getProductDisplayValue;
  const getAditionPriceDisplayValue = getProductDisplayValue;
  const getAditionSupressionDisplayValue = getProductDisplayValue;
  const getAditionModifyItemDisplayValue = getProductDisplayValue;
  const getAditionContractAmendmentItemsDisplayValue = getProductDisplayValue;
  const getAditionContractAmendmentJobsDisplayValue = getProductDisplayValue;
  const getAditionRegisterItemsDisplayValue = getProductDisplayValue;
  const getAditionRegisterJobsDisplayValue = getProductDisplayValue;

  // * Adicione este useEffect para limpar os dados quando um tipo de evento for desmarcado
  const previousSelectedEventTypes = useRef([]);

  useEffect(() => {
    // Acessa o campo específico dentro do formData
    const currentSelected = eventFormData?.event?.contract_event_type_id || [];
    const currentSelectedValues = currentSelected.map((opt) => opt.value);
    const previousSelectedValues = previousSelectedEventTypes.current;

    const cleanupMap = {
      1: () => {
        setAditionRegisterItems([]);
        setAditionRegisterJobs([]);
      },
      2: () =>
        setAditionTermFormData(setDefaultFieldValues([aditionTermSection])),
      3: () => setAditionQuantity([]),
      4: () => setAditionPrice([]),
      5: () => setAditionSupression([]),
      6: () => setAditionModifyItem([]),
      7: () => {
        setAditionContractAmendmentItems([]);
        setAditionContractAmendmentJobs([]);
      },
    };

    previousSelectedValues.forEach((prevValue) => {
      if (!currentSelectedValues.includes(prevValue)) {
        const cleanupFunction = cleanupMap[prevValue];
        if (cleanupFunction) {
          cleanupFunction();
        }
      }
    });

    previousSelectedEventTypes.current = currentSelectedValues;

    // A dependência agora é apenas no campo que realmente importa
  }, [eventFormData?.event?.contract_event_type_id]);

  return {
    form: {
      errors: formErrors,
      setErrors: setFormErrors,
    },
    event: {
      section: eventSection.section,
      description: eventSection.description,
      fields: eventSection.fields,
      formData: eventFormData,
      fieldsState: eventFieldsState,
      setFieldsState: setEventFieldsState,
      handleChange: handleEventChange,
      setFormData: setEventFormData,
      resetForm: resetEventForm,
      getOptions: getEventOptions,
      getSelectedValue: getEventSelectedValue,
      getSelectMeta: getEventSelectMeta,
      selects: {
        eventTypesSelect,
      },
    },
    aditionRegister: {
      section: aditionRegisterSection.section,
      description: aditionRegisterSection.description,
      fields: aditionRegisterSection.fields,
      formData: contractFormData,
      setFormData: setContractFormData,
      handleChange: handleContractChange,
      getOptions: getContractOptions,
      getSelectedValue: getContractSelectedValue,
      getSelectMeta: getContractSelectMeta,

      items: {
        section: aditionRegisterSection.itemSection.section,
        description: aditionRegisterSection.itemSection.description,
        fields: aditionRegisterSection.itemSection.fields,
        formData: aditionRegisterItemsFormData,
        handleChange: handleAditionRegisterItemChange,
        setFormData: setAditionRegisterItemsFormData,
        aditionRegisterItems,
        setAditionRegisterItems,
        getOptions: aditionRegisterItemsOptions,
        getSelectedValue: aditionRegisterItemsSelectedValue,
        getSelectMeta: aditionRegisterItemsSelectMeta,
        handleAditionRegisterItemsChange, // * Para o componente de adição de item modificado, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
        handleSaveAditionRegisterItems, // * Para salvar as alterações feitas no formulário de adição de item modificado
        initializeAditionRegisterItemsForm, // * Para inicializar o formulário de adição de item modificado com os dados de um item existente
        renderAditionRegisterItemsFormBody, // * Para renderizar o corpo do formulário de adição de item modificado
        getAditionRegisterItemsDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de item modificado
      },
      jobs: {
        section: aditionRegisterSection.jobSection.section,
        description: aditionRegisterSection.jobSection.description,
        fields: aditionRegisterSection.jobSection.fields,
        formData: aditionRegisterJobsFormData,
        handleChange: handleAditionRegisterJobChange,
        setFormData: setAditionRegisterJobsFormData,
        aditionRegisterJobs,
        setAditionRegisterJobs,
        getOptions: aditionRegisterJobsOptions,
        getSelectedValue: aditionRegisterJobsSelectedValue,
        getSelectMeta: aditionRegisterJobsSelectMeta,
        handleAditionRegisterJobsChange, // * Para o componente de adição de item modificado, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
        handleSaveAditionRegisterJobs, // * Para salvar as alterações feitas no formulário de adição de item modificado
        initializeAditionRegisterJobsForm, // * Para inicializar o formulário de adição de item modificado com os dados de um item existente
        renderAditionRegisterJobsFormBody, // * Para renderizar o corpo do formulário de adição de item modificado
        getAditionRegisterJobsDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de item modificado
      },
    },
    aditionTerm: {
      section: aditionTermSection.section,
      description: aditionTermSection.description,
      fields: aditionTermSection.fields,
      formData: aditionTermFormData,
      setFormData: setAditionTermFormData,
      handleChange: handleAditionTermChange,
      renderAditionTermFormBody, // * Para renderizar o corpo do formulário de aditivo de termo
    },
    aditionQuantity: {
      section: aditionQuantitySection.section,
      description: aditionQuantitySection.description,
      fields: aditionQuantitySection.fields,
      formData: aditionQuantityFormData,
      handleChange: handleAditionQuantityChange,
      setFormData: setAditionQuantityFormData,
      fieldsState: aditionQuantityFieldsState,
      setFieldsState: setAditionQuantityFieldsState,
      aditionQuantity,
      setAditionQuantity,
      getOptions: getAditionQuantityOptions,
      getSelectedValue: getAditionQuantitySelectedValue,
      getSelectMeta: getAditionQuantitySelectMeta,
      handleAditionsQuantityChange, // * Para o componente de adição de quantidade, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
      handleSaveAditionQuantity, // * Para salvar as alterações feitas no formulário de adição de quantidade
      initializeAditionQuantityForm, // * Para inicializar o formulário de adição de quantidade com os dados de um item existente
      renderAditionQuantityFormBody, // * Para renderizar o corpo do formulário de adição de quantidade
      getAditionQuantityDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de quantidade
    },
    aditionPrice: {
      section: aditionPriceSection.section,
      description: aditionPriceSection.description,
      fields: aditionPriceSection.fields,
      formData: aditionPriceFormData,
      handleChange: handleAditionPriceChange,
      setFormData: setAditionPriceFormData,
      fieldsState: aditionPriceFieldsState,
      setFieldsState: setAditionPriceFieldsState,
      aditionPrice,
      setAditionPrice,
      getOptions: getAditionPriceOptions,
      getSelectedValue: getAditionPriceSelectedValue,
      getSelectMeta: getAditionPriceSelectMeta,
      handleAditionsPriceChange, // * Para o componente de adição de preço, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
      handleSaveAditionPrice, // * Para salvar as alterações feitas no formulário de adição de preço
      initializeAditionPriceForm, // * Para inicializar o formulário de adição de preço com os dados de um item existente
      renderAditionPriceFormBody, // * Para renderizar o corpo do formulário de adição de preço
      getAditionPriceDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de preço
    },
    aditionSupression: {
      section: aditionSupressionSection.section,
      description: aditionSupressionSection.description,
      fields: aditionSupressionSection.fields,
      formData: aditionSupressionFormData,
      handleChange: handleAditionSupressionChange,
      setFormData: setAditionSupressionFormData,
      fieldsState: aditionSupressionFieldsState,
      setFieldsState: setAditionSupressionFieldsState,
      aditionSupression,
      setAditionSupression,
      getOptions: getAditionSupressionOptions,
      getSelectedValue: getAditionSupressionSelectedValue,
      getSelectMeta: getAditionSupressionSelectMeta,
      handleAditionsSupressionChange, // * Para o componente de adição de supressão, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
      handleSaveAditionSupression, // * Para salvar as alterações feitas no formulário de adição de supressão
      initializeAditionSupressionForm, // * Para inicializar o formulário de adição de supressão com os dados de um item existente
      renderAditionSupressionFormBody, // * Para renderizar o corpo do formulário de adição de supressão
      getAditionSupressionDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de supressão
    },
    aditionModifyItem: {
      section: aditionModifyItemSection.section,
      description: aditionModifyItemSection.description,
      fields: aditionModifyItemSection.fields,
      formData: aditionModifyItemFormData,
      handleChange: handleAditionModifyItemChange,
      setFormData: setAditionModifyItemFormData,
      aditionModifyItem,
      setAditionModifyItem,
      getOptions: getAditionModifyItemOptions,
      getSelectedValue: getAditionModifyItemSelectedValue,
      getSelectMeta: getAditionModifyItemSelectMeta,
      handleAditionsModifyItemChange, // * Para o componente de adição de item modificado, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
      handleSaveAditionModifyItem, // * Para salvar as alterações feitas no formulário de adição de item modificado
      initializeAditionModifyItemForm, // * Para inicializar o formulário de adição de item modificado com os dados de um item existente
      renderAditionModifyItemFormBody, // * Para renderizar o corpo do formulário de adição de item modificado
      getAditionModifyItemDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de item modificado
    },
    aditionContractAmendment: {
      section: aditionContractAmendmentSection.section,
      description: aditionContractAmendmentSection.description,
      fields: aditionContractAmendmentSection.fields,
      formData: aditionContractAmendmentFormData,
      handleChange: handleAditionContractAmendmentChange,
      setFormData: setAditionContractAmendmentFormData,

      items: {
        section: aditionContractAmendmentSection.itemSection.section,
        description: aditionContractAmendmentSection.itemSection.description,
        fields: aditionContractAmendmentSection.itemSection.fields,
        formData: aditionContractAmendmentItemsFormData,
        handleChange: handleAditionContractAmendmentItemChange,
        setFormData: setAditionContractAmendmentItemsFormData,
        aditionContractAmendmentItems,
        setAditionContractAmendmentItems,
        getOptions: aditionContractAmendmentItemsOptions,
        getSelectedValue: aditionContractAmendmentItemsSelectedValue,
        getSelectMeta: aditionContractAmendmentItemsSelectMeta,
        handleAditionContractAmendmentItemsChange, // * Para o componente de adição de item modificado, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
        handleSaveAditionContractAmendmentItems, // * Para salvar as alterações feitas no formulário de adição de item modificado
        initializeAditionContractAmendmentItemsForm, // * Para inicializar o formulário de adição de item modificado com os dados de um item existente
        renderAditionContractAmendmentItemsFormBody, // * Para renderizar o corpo do formulário de adição de item modificado
        getAditionContractAmendmentItemsDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de item modificado
      },
      jobs: {
        section: aditionContractAmendmentSection.jobSection.section,
        description: aditionContractAmendmentSection.jobSection.description,
        fields: aditionContractAmendmentSection.jobSection.fields,
        formData: aditionContractAmendmentJobsFormData,
        handleChange: handleAditionContractAmendmentJobChange,
        setFormData: setAditionContractAmendmentJobsFormData,
        aditionContractAmendmentJobs,
        setAditionContractAmendmentJobs,
        getOptions: aditionContractAmendmentJobsOptions,
        getSelectedValue: aditionContractAmendmentJobsSelectedValue,
        getSelectMeta: aditionContractAmendmentJobsSelectMeta,
        handleAditionContractAmendmentJobsChange, // * Para o componente de adição de item modificado, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
        handleSaveAditionContractAmendmentJobs, // * Para salvar as alterações feitas no formulário de adição de item modificado
        initializeAditionContractAmendmentJobsForm, // * Para inicializar o formulário de adição de item modificado com os dados de um item existente
        renderAditionContractAmendmentJobsFormBody, // * Para renderizar o corpo do formulário de adição de item modificado
        getAditionContractAmendmentJobsDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de item modificado
      },
    },
  };
};
